import { forwardRef } from 'react';
import type { ComponentPropsWithoutRef, HTMLAttributes } from 'react';
import { cn } from '~/lib/utils';

/**
 * Inline code chip — for tool IDs, user IDs, env-var names, short tags.
 *
 *   <Code>outlook__list_mail</Code>
 *   <Code size="xs">user_overrides</Code>
 *   <Code variant="ghost">no-bg variant</Code>
 *
 * Theme-aware via shadcn tokens (`bg-muted`, `border-border`, `text-foreground`)
 * so dark mode just works. Use <CodeBlock> for multi-line snippets.
 */
const SIZE_CLASSES = {
  xs: 'text-[length:var(--text-2xs)] px-1.5 py-0.5',
  sm: 'text-[length:var(--text-2xs)] px-1.5 py-0.5',
  md: 'text-xs px-2 py-1',
} as const;

const VARIANT_CLASSES = {
  default: 'border border-border bg-muted/60 text-muted-foreground',
  solid: 'border border-border bg-muted text-foreground',
  ghost: 'text-muted-foreground',
} as const;

interface CodeProps extends ComponentPropsWithoutRef<'code'> {
  size?: keyof typeof SIZE_CLASSES;
  variant?: keyof typeof VARIANT_CLASSES;
}

export const Code = forwardRef<HTMLElement, CodeProps>(function Code(
  { size = 'sm', variant = 'default', className, ...rest },
  ref,
) {
  return (
    <code
      ref={ref}
      className={cn(
        'inline-flex items-center rounded font-mono whitespace-nowrap leading-none',
        SIZE_CLASSES[size],
        VARIANT_CLASSES[variant],
        className,
      )}
      {...rest}
    />
  );
});

/**
 * Block code — multi-line, scrollable. Read-only display of JSON / errors /
 * stack traces. Inherits theme tokens like Code. For editable code use
 * `<CodeEditor>` instead.
 */
interface CodeBlockProps extends HTMLAttributes<HTMLPreElement> {
  /** When true, wraps long lines instead of horizontal scroll. */
  wrap?: boolean;
  /** Max height before scrolling kicks in. Default ~12rem. */
  maxHeight?: number;
}

export const CodeBlock = forwardRef<HTMLPreElement, CodeBlockProps>(function CodeBlock(
  { wrap = false, maxHeight, className, children, style, ...rest },
  ref,
) {
  return (
    <pre
      ref={ref}
      className={cn(
        'rounded-md border border-border bg-muted/40 text-foreground/90',
        'px-3 py-2 text-[length:var(--text-2xs)] leading-[1.5] font-mono',
        wrap ? 'whitespace-pre-wrap break-words' : 'whitespace-pre overflow-x-auto',
        'overflow-y-auto',
        className,
      )}
      style={{ maxHeight: maxHeight ?? 192, ...style }}
      {...rest}
    >
      {children}
    </pre>
  );
});
